/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, createContext, forwardRef } from 'react';
import { type ContextValue, DEFAULT_SLOT, Form as FormCore, type FormProps as FormCoreProps, Provider, useContextProps } from 'react-aria-components';
import { breakpoints } from '../../constants.stylex';
import { useMediaQuery } from '../../hooks/use-media-query/use-media-query.hook';
import { RewindStrokeIcon } from '../../icons/rewind-stroke/rewind-stroke.icon';
import { ButtonContext } from '../button/button.component';
import { Icon } from '../icon/icon.component';
import { type LabelProps } from '../label/label.component';
import { TEXT_BUTTON_SLOTS, TextButtonContext } from '../text-button/text-button.component';
import { Text } from '../text/text.component';
import { ToggleButtonContext } from '../toggle-button/toggle-button.component';
import { ViewContext } from '../view/view.component';

interface FormRenderProps {
  fieldStyles: stylex.StyleXStyles;
  fullWidthStyle: stylex.StyleXStyles;
  rowViewStyle: stylex.StyleXStyles;
  columnViewStyle: stylex.StyleXStyles;
  underFieldStyle: stylex.StyleXStyles;
  singleElementStyle: stylex.StyleXStyles;
  narrowInputStyle: stylex.StyleXStyles;
  labelColor: LabelProps['color'];
}

interface FormProps extends Omit<FormCoreProps, 'className' | 'style' | 'children'> {
  style?: stylex.StyleXStyles;
  children?: ((renderProps: FormRenderProps) => React.ReactNode) | React.ReactNode;
}

const FormContext = createContext<ContextValue<FormProps, HTMLFormElement>>({});

const Form = forwardRef((props: FormProps, ref: ForwardedRef<HTMLFormElement>) => {
  [props, ref] = useContextProps(props, ref, FormContext);

  const { style, children, ...coreProps } = props;

  const isSmallScreen = useMediaQuery(breakpoints.small);

  const renderProps: FormRenderProps = {
    fieldStyles: renderPropStyles.fieldStyles,
    fullWidthStyle: renderPropStyles.fullWidthStyle,
    rowViewStyle: renderPropStyles.rowViewStyle,
    columnViewStyle: renderPropStyles.columnViewStyle,
    underFieldStyle: renderPropStyles.underFieldStyle,
    singleElementStyle: renderPropStyles.singleElementStyle,
    narrowInputStyle: renderPropStyles.narrowInputStyle,
    labelColor: isSmallScreen ? 'primary' : 'tertiary',
  };

  return (
    <FormCore ref={ref} {...stylex.props(styles.base, style)} {...coreProps}>
      <Provider values={[
        [ViewContext, {
          slots: {
            [DEFAULT_SLOT]: {},
            [FORM_SLOTS.FIELD_VIEW]: {
              style: viewStyles.field,
            },
            [FORM_SLOTS.ACTIONS_VIEW]: {
              style: viewStyles.actions,
            },
          }
        }],
        [ToggleButtonContext, {
          variant: 'outlined',
          size: 36,
        }],
        [ButtonContext, {
          isRounded: true,
          size: 40,
        }],
        [TextButtonContext, {
          slots: {
            [DEFAULT_SLOT]: {},
            [FORM_SLOTS.RETRY_TEXT_BUTTON]: {
              size: 48,
              variant: 'primary',
              safeArea: 'none',
              style: [renderPropStyles.underFieldStyle, renderPropStyles.singleElementStyle, retryTextButtonStyle.base],
              children: (
                <>
                  <Icon slot={TEXT_BUTTON_SLOTS.LEFT_ICON}>
                    <RewindStrokeIcon />
                  </Icon>
                  <Text>Failed to load. Retry</Text>
                </>
              )
            },
            [FORM_SLOTS.ADD_TEXT_BUTTON]: {
              size: 48,
              variant: 'primary',
              safeArea: 'none',
              style: [renderPropStyles.underFieldStyle, renderPropStyles.singleElementStyle, retryTextButtonStyle.base],
            }
          }
        }]
      ]}>
        {typeof children === 'function' ? children(renderProps) : children}
      </Provider>
    </FormCore>
  );
});

const COLUMN_GAP = 20;

const styles = stylex.create({
  base: {
    display: 'grid',
    gridTemplateColumns: {
      default: 'minmax(124px, auto) minmax(0, 648px)',
      [breakpoints.small]: '100%',
    },
    inlineSize: 'max-content',
    rowGap: 24,
    maxWidth: 592,
    paddingBottom: 8,
    width: '100%',
  },
});

const renderPropStyles = stylex.create({
  fullWidthStyle: {
    gridColumn: '1 / -1',
  },
  fieldStyles: {
    gridColumn: '1 / -1',
    alignItems: 'baseline',
    columnGap: COLUMN_GAP,
    display: 'grid',
    gridTemplateColumns: 'subgrid',
    rowGap: 6,
  },
  underFieldStyle: {
    gridColumn: {
      default: '2',
      [breakpoints.small]: '1'
    }
  },
  singleElementStyle: {
    marginLeft: {
      default: COLUMN_GAP / 2,
      [breakpoints.small]: 0,
    }
  },
  rowViewStyle: {
    gap: 12,
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
  },
  columnViewStyle: {
    gap: 12,
    display: 'flex',
    flexDirection: 'column',
  },
  narrowInputStyle: {
    width: 210,
  },
});

const viewStyles = stylex.create({
  field: {
    gridColumn: '1 / -1',
    alignItems: 'center',
    display: 'grid',
    gridTemplateColumns: 'subgrid',
  },
  actions: {
    gap: 12,
    gridColumn: {
      default: '2',
      [breakpoints.small]: '1',
    },
    display: 'flex',
    flexDirection: {
      default: 'row',
      [breakpoints.small]: 'column',
    },
    justifyContent: 'flex-start',
    paddingLeft: {
      default: 12,
      [breakpoints.small]: 0,
    },
  }
});

const retryTextButtonStyle = stylex.create({
  base: {
    justifyContent: 'flex-start',
    height: 44,
  }
});

const FORM_SLOTS = {
  FIELD_VIEW: 'field-view',
  ACTIONS_VIEW: 'actions-view',
  RETRY_TEXT_BUTTON: 'retry-text-button',
  ADD_TEXT_BUTTON: 'add-text-button'
};

export { FORM_SLOTS, Form, FormContext, type FormProps };
